/*
 * Subscription.ts (AbstractWebCommon)
 *
 * Copyright © 2023 InstaLOD GmbH - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * This file and all its contents are proprietary and confidential.
 *
 * Maintained by Rafael Rodrigues, 2023
 *
 * @file Subscription.ts
 * @author Rafael Rodrigues
 * @copyright 2023 InstaLOD GmbH. All rights reserved.
 * @section License
 */

import { IProduct } from './Product';

/**
 * @interface ISubscription
 */
export interface ISubscription {
  _id?: string /**< Subscription ID generated by Mongoose */
  billingPeriodStartDate: string /**< Billing period start. Field from Braintree built-in interface. */;
  billingPeriodEndDate: string; /**< Billing period end. Field from Braintree built-in interface. */
  price?: string | undefined /**< Billing price. Field from Braintree built-in interface. */;
  priceAsNumber?: number /**< Numeric mirror of `price`, kept in sync by the Subscription model's pre-save hook. Used for numeric comparisons/sorting and seat-change pro-ration. */;
  numberOfBillingCycles?: number | undefined /**< Total of billing cycles. Field from Braintree built-in interface. */;
  currentBillingCycle: number; /**< Number of current billing cycle. Field from Braintree built-in interface. */
  braintreeSubscriptionID?: string /**< Subscription ID generated by Braintree */
  createdAt?: Date /**< Created At */
  updatedAt?: Date /**< Updated At */
  planID?: string /**< Subscription plan ID */
  status: string /**< Subscription status */
  transactionUUID?: string /**< Reference to the created transaction */
  paymentMethodToken?: string /**< Payment token, used for security between HTTPS communication  */
  nextBillingDate?: string /**< Next day user will be billed */
  customer?: any /**< Customer who made the subscription transaction */
  customerFullName?: string /**< Customer who made the subscription transaction */
  userUUID?: string; /**< User UUID who made the subscription transaction */
  planName?: string; /**< Subscription plan name */
  braintreeTransactions?: ISubscriptionBraintreeTransactions[] /**< Transactions that Braintree creates for the particular subscription */
  lockUUID?: string; /**< Lock UUID for lock logic. Used for the subscription job to avoid multiple changes at the same time */
  lockExpiryDate?: Date; /**< Lock expiry date for lock logic. Used for the subscription job to avoid multiple changes at the same time */
  subscriptionEndDate?: Date; /**< Subscription end date */
  subscriptionStartDate?: Date; /**< Subscription start date */
  localSubscriptionStatus?: string; /**< Local subscription status */
  invoiceNo?: number; /**< Invoice number of the linked transaction */
  transaction?: any; /**< Transaction object */
  product?: IProduct; /**< Purchased Product details */
  allTransactionsInvoiceDetails?: IInvoiceDetails[]; /**< Invoice number of all transactions with invoice link related to subscription */
  isTerminated?: boolean; /**< To check subscription is terminated or not */
  isPastDueEmailSent?: boolean /**< Indicates if the Past Due email template was sent. In case not, the Subscription job tries to send it again */;
  isPauseDueToFailedPaymentEmailSent?: boolean /**< Indicates if the pause due to failed payment email template was sent. In case not, the Subscription job tries to send it again */;
  pastDueReason?: string /**< Indicates the reason when the subscription changes their status to Past Due. */;
  lastBillingDate?: Date /**< Indicates the last billing date of the subscription. Only available for paid subscriptions. */;
  pendingRefundRequest?: boolean /**< True if there is a refund request awaiting admin review for THIS subscription. Used to disable the request option and show a "Refund requested" state on this row. */;
  seatCount?: number /**< Number of billed seats for a seat-based subscription. The subscription price is product.price × seatCount. Undefined/1 for non-seat-based subscriptions. Changed via the change-seats flow with pro-rated charge/refund. */;
}

/**
 * Direction of a seat-count change.
 * @enum SeatChangeDirection
 */
export enum SeatChangeDirection {
  Upgrade = 'Upgrade' /**< The new seat count (and therefore price) is higher; a pro-rated amount is charged. */,
  Downgrade = 'Downgrade' /**< The new seat count (and therefore price) is lower; a pro-rated amount is refunded. */
}

/**
 * Request body to change the seat count of a seat-based subscription.
 * @interface IChangeSeatCountRequest
 */
export interface IChangeSeatCountRequest {
  newSeatCount: number /**< The desired number of billed seats. */;
}

/**
 * Preview of the financial impact of a seat-count change, computed server-side and shown to the
 * user before they confirm. All monetary values are strings in "0.00" format, in the subscription currency.
 * @interface ISeatChangePreview
 */
export interface ISeatChangePreview {
  currentSeatCount: number /**< Current number of billed seats. */;
  newSeatCount: number /**< Requested number of billed seats. */;
  currentPrice: string /**< Current per-cycle subscription price. */;
  newPrice: string /**< New per-cycle subscription price after the change. */;
  direction: SeatChangeDirection /**< Whether this change results in a charge (Upgrade) or refund (Downgrade). */;
  proratedAmount: string /**< The pro-rated amount charged (Upgrade) or refunded (Downgrade) immediately for the remainder of the current billing period. */;
  effectiveDate: string /**< ISO date the new price applies from (next billing cycle; the anchor is preserved). */;
  daysRemaining: number /**< Whole days remaining in the current billing period, used for the pro-ration. */;
}

/**
 * Braintree built-in interface.
 * @interface TransactionStatus
 */
export type TransactionStatus =
        | 'authorization_expired'
        | 'authorized'
        | 'authorizing'
        | 'settlement_confirmed'
        | 'settlement_pending'
        | 'settlement_declined'
        | 'failed'
        | 'gateway_rejected'
        | 'processor_declined'
        | 'settled'
        | 'settling'
        | 'submitted_for_settlement'
        | 'voided';

/**
 * @interface ISubscriptionBraintreeTransactions
 */
export interface ISubscriptionBraintreeTransactions {
  status: TransactionStatus /**< Transaction status. Field from Braintree built-in interface. */;
  type: string /**< Transaction type. Sale when is a debit or Credit when is a refund. Field from Braintree built-in interface. */;
  amount: string /**< Transaction amount. Field from Braintree built-in interface. */;
  refundIds?: string[] | undefined /**< Reference to the created transactions which will refund user. Value is null in the refunded transaction record. Field from Braintree built-in interface. */;
  refundedTransactionId?: string | undefined /**< Reference to the transaction which the refund was requested. Value is null in the requested refunded transaction. Field from Braintree built-in interface. */;
  createdAt: string /**< Defines when the transaction was created. */
  updatedAt: string /**< Defines when the transaction was last updated. */;
  transactionBraintreeID: string /**< Transaction ID generated by Braintree. */
  billingPeriodStartDate: Date /**< Defines the start date of the recurring billing period. */
  billingPeriodEndDate: Date /**< Defines the end date of the recurring billing period. */
}

/**
 * @interface IGetAllSubscriptionResponse
 */
export interface IGetAllSubscriptionResponse {
  subscriptions: ISubscription[] /**< Subscription list retrieved from DB */
  totalRecords: number /**< Subscription count retrieved from DB */
}

/**
 * @interface IGetSubscriptionByUserUUIDResponse
 */
export interface IGetSubscriptionByUserUUIDResponse extends IGetAllSubscriptionResponse {}

/**
 * @interface IInvoiceDetails
 */
export interface IInvoiceDetails {
  invoiceNo: number; /**< Invoice number of the transaction */
  invoiceLink: string; /**< Link to invoice PDF */
}

/**
 * @interface IGetSubscriptionPastDueReasons
 */
export interface IGetSubscriptionPastDueReasonsControllerResponse {
  transactionUUID: string; /**< Transaction UUID */
  pastDueReason: string; /**< Respective past due reason of the transaction UUID */
}

/**
 * @interface ICheckActiveSubscriptionsEcommerceServiceReturn
 */
export interface ICheckActiveSubscriptionsEcommerceServiceReturn {
  activatedSubscriptions: ISubscription[] /**< List of active subscriptions and the subscription type. */
  nonActivatedSubscriptions: ISubscription[] /**< List of non active subscriptions and the subscription type.  */
}